import React, { useEffect, useState } from 'react';
import cn from 'classnames';
import { MaskedInput } from 'react-text-mask-modern';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import { changeColorActions } from '../../actions';
import { levelSpecifications, pxTalkLevelTypes } from '../../constants';
import { ButtonSwitch } from '../controls';
import { stringUtils } from '../../utils';
import IconSVG from '../../styles/svg-icons';
import { useAppDispatch } from '../../effects/useAppDispatch';
import { useAppSelector } from '../../effects/useAppSelector';

const decimalMask = createNumberMask({
    prefix: '',
    suffix: '',
    includeThousandsSeparator: false,
    allowDecimal: true,
    allowNegative: true,
    decimalLimit: null
});

interface ChangeColorProps {
    saveButtonText?: string;
    onSave: () => void;
    onCancel?: () => void;
    onDelete?: () => void;
}

export function ChangeColor({ saveButtonText, onSave, onCancel, onDelete }: ChangeColorProps) {
    const dispatch = useAppDispatch();

    const ticker = useAppSelector(s => s.changeColor.ticker);
    const color = useAppSelector(s => s.changeColor.color);
    const errors = useAppSelector(s => s.changeColor.errors) ?? {};
    const isRequestingEvalDm = useAppSelector(s => s.changeColor.isRequestingEvalDm);

    const [changed, setChanged] = useState(false);

    const evalDmFromStore = color?.evalDmResponse?.dm == null ? '' : String(color.evalDmResponse.dm);
    const [evalDmValue, setEvalDmValue] = useState(evalDmFromStore);

    useEffect(() => {
        setEvalDmValue(evalDmFromStore);
    }, [evalDmFromStore]);

    if (!color) return null;



    const formControlClasses = cn('form-control', {
        'is-invalid': !!errors.level || !!errors.color
    });

    function handleLevelChange(e: React.ChangeEvent<HTMLInputElement>) {
        setChanged(true);
        dispatch(changeColorActions.levelChange(e.target.value));
    }

    function handleLevelBlur() {
        if (changed) {
            setChanged(false);
            dispatch(changeColorActions.normalizeLevel(color!.level, color!.levelType));
        }
    }

    function handleDmChange(e: React.ChangeEvent<HTMLInputElement>) {
        dispatch(changeColorActions.setDm(e.target.value));
    }

    function handleEvalDmBlur() {
        const parsed = parseFloat(evalDmValue);
        dispatch(changeColorActions.evalDmChange(isNaN(parsed) ? undefined : parsed));
    }

    function handleSave() {
        dispatch(changeColorActions.waitNormalizing(onSave));
    }

    function handleCancel() {
        dispatch(changeColorActions.reset());
        onCancel?.();
    }

    return (
        <div className="sidebar-panel sidebar-panel-color">
            <div className="sidebar-panel-header">
                {!stringUtils.isNullOrEmpty(ticker) && <h2>Color for {ticker}</h2>}
                {stringUtils.isNullOrEmpty(ticker) && <h2>Change color</h2>}
                <button className="btn-close flex-none flex-item-right" onClick={() => dispatch(changeColorActions.reset())}>
                    <IconSVG name="close" width={16} height={16} />
                </button>
            </div>
            <div className="sidebar-panel-content">
                <div className="form-row form-color-wrapper">
                    <div className="form-item">
                        <ButtonSwitch
                            sourceItems={[{ key: true, title: 'Traded' }, { key: false, title: 'DNT' }]}
                            selectedValue={color.traded}
                            onSelectionChange={(traded: boolean) => dispatch(changeColorActions.tradedChange(traded))}
                            className="control-group-type01"
                        />
                    </div>
                    <div className="form-item flex-row">
                        <ButtonSwitch
                            sourceItems={[levelSpecifications.best, levelSpecifications.cover]}
                            selectedValue={color.levelSpecificationType}
                            onSelectionChange={(levelSpecification: any) => dispatch(changeColorActions.levelSpecificationChange(levelSpecification))}
                            className="control-group-type01"
                        />
                    </div>
                    <div className="form-item form-item-color">
                        <label className="form-label">Color</label>
                        <div className="form-control-wrapper">
                            <input
                                type="text"
                                className={formControlClasses}
                                maxLength={49}
                                autoComplete="off"
                                value={color.level || ''}
                                onChange={handleLevelChange}
                                onBlur={handleLevelBlur}
                            />
                            {(!!errors.level || !!errors.color) && <div className="form-error">{errors.level || errors.color}</div>}
                        </div>
                        {color.normalizedLevel != null && <div className="sub-text"><span className="bold">{color.normalizedLevel}</span> Normalized</div>}
                    </div>
                    <div className="form-item">
                        <ButtonSwitch
                            sourceItems={[pxTalkLevelTypes.price, pxTalkLevelTypes.DM]}
                            selectedValue={color.levelType}
                            onSelectionChange={(levelType: any) => dispatch(changeColorActions.levelTypeChange(levelType))}
                            className="control-group-type01"
                        />
                    </div>
                    <div className="form-item form-item-color">
                        <label className="form-label">DM</label>
                        <div className="form-control-wrapper">
                            <MaskedInput
                                className="form-control"
                                mask={decimalMask}
                                value={String(color.dm || '')}
                                onChange={handleDmChange}
                            />
                        </div>
                    </div>
                </div>
                <div className="form-row form-color-wrapper">
                    <div className="form-item form-item-color">
                        <label className="form-label">Eval DM</label>
                        <div className="form-control-wrapper">
                            <MaskedInput
                                className="form-control"
                                mask={decimalMask}
                                value={evalDmValue}
                                onChange={e => setEvalDmValue(e.target.value)}
                                onBlur={handleEvalDmBlur}
                            />
                        </div>
                    </div>
                    <div className="form-item">
                        <button
                            className="btn btn-ghost"
                            type="button"
                            disabled={!color.level || isRequestingEvalDm}
                            onClick={() => dispatch(changeColorActions.requestEvalDm())}
                        >
                            REQUEST EVAL DM
                        </button>
                    </div>
                </div>
            </div>
            <div className="sidebar-panel-footer">
                {!color.isNew && onDelete != null &&
                    <button className="btn-link btn-danger" type="button" onClick={onDelete}>
                        <i className="icon icon-delete" />
                        Delete
                    </button>
                }
                <div className="flex-item-right">
                    <button className="btn btn-ghost" type="button" onClick={handleCancel}>CANCEL</button>
                    <button className="btn btn-main" type="button" onClick={handleSave}>{saveButtonText || 'SAVE CHANGES'}</button>
                </div>
            </div>
        </div>
    );
}
